import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTariff, useTariffRates } from './useTariffs';
import { useChild } from './useChildren';
import type { PartialServicePatternEntry, Service, EnhancedMultiGroupCostResult, PeriodCostBreakdown, DiscountsData, AgeType } from '@/types/billing';
import { useLingui } from '@lingui/react';
import { normalizeAndValidateRates, findMatchingRateStrict } from '@/hooks/billing/rateUtils';
import { deriveBillingParams } from '@/hooks/billing/settingsUtils';
import { extractAgeTransitionConfig, AGE_TRANSITION_DEFAULTS } from './useContractAgeTransitionConfig';
import { dayNameToNumber, countWeekdaysInPeriod } from '@/hooks/billing/dateUtils';
import { detectServiceOverlaps as detectOverlapsUtil } from '@/hooks/billing/validationUtils';

interface MultiGroupServicePattern {
  [groupType: string]: Record<string, PartialServicePatternEntry[]>;
}

interface TariffVersions {
  [groupType: string]: string;
}

// Enhanced cost calculation function that supports age transitions
interface UseEnhancedCostCalculationOptions {
  includeDiscounts?: boolean;
  calculationDate?: string;
  useEnhancedAPI?: boolean;
  /**
   * Selects the subsidised pricing stack (subsidised tariff rows, subsidised
   * week factors, subsidised age threshold). Derive it with
   * `usesSubsidisedRates` (`@/utils/contracts/subsidisedRates`): only a
   * subsidised contract under a `zurich_bf` office uses this stack.
   *
   * ⚠ An on-top Subventionszuschuss (`subsidy_supplement_amount`) must NOT
   * affect this flag: the parent is billed the full normal Tarif and the flat
   * Zuschuss is billed to the office on top (not parent-visible, never
   * subtracted from the parent's displayed cost).
   */
  isSubsidised?: boolean;
  // Keep a consistent age type within the preview month (no within-month split)
  lockAgeToCalculationMonth?: boolean;
  contractStartDate?: string;
  contractEndDate?: string;
  fullContractView?: boolean;
}

export function useMultiGroupCostCalculation(
  servicePattern: MultiGroupServicePattern,
  tariffVersions: TariffVersions,
  childId: string,
  services: Service[],
  discounts?: DiscountsData,
  options?: UseEnhancedCostCalculationOptions
): EnhancedMultiGroupCostResult {
  const { i18n } = useLingui();
  // Stabilize empty inputs to avoid dependency thrash and render loops
  const EMPTY_SERVICE_PATTERN: MultiGroupServicePattern = useMemo(() => ({}), []);
  const EMPTY_TARIFF_VERSIONS: TariffVersions = useMemo(() => ({}), []);

  const normalizedServicePattern = useMemo<MultiGroupServicePattern>(() => {
    if (!servicePattern || Object.keys(servicePattern).length === 0) {
      return EMPTY_SERVICE_PATTERN;
    }
    return servicePattern;
  }, [servicePattern, EMPTY_SERVICE_PATTERN]);

  const normalizedTariffVersions = useMemo<TariffVersions>(() => {
    if (!tariffVersions || Object.keys(tariffVersions).length === 0) {
      return EMPTY_TARIFF_VERSIONS;
    }
    return tariffVersions;
  }, [tariffVersions, EMPTY_TARIFF_VERSIONS]);

  // Resolve the age-transition config (BABY/CHILD threshold + timing) from the
  // contract's tariff version instead of the org-level billing settings. The
  // age computation here is single-threshold (as it always was), so we source it
  // from the first assigned tariff version, falling back to 24/18/FOLLOWING_MONTH.
  const ageTariffVersionId = useMemo(
    () => Object.values(normalizedTariffVersions)[0] ?? '',
    [normalizedTariffVersions],
  );
  const { data: ageTariffVersion } = useTariff(ageTariffVersionId);
  const ageTransitionConfig = useMemo(
    () => (ageTariffVersionId && ageTariffVersion ? extractAgeTransitionConfig(ageTariffVersion) : AGE_TRANSITION_DEFAULTS),
    [ageTariffVersionId, ageTariffVersion],
  );
  const [enhancedResult, setEnhancedResult] = useState<EnhancedMultiGroupCostResult | null>(null);
  const enhancedResultHashRef = useRef<string | null>(null);

  // Fetch the specific child by ID to get birthdate for age calculation
  const { data: childData } = useChild(childId);

  // Determine if we should use enhanced calculations
  const shouldUseEnhanced = true;

  // Shared helper to parse birthdate and derive age information
  function parseBirthdateAndAgeType(
    birthdateStr?: string,
    referenceDate?: Date,
    thresholdMonths?: number
  ): { birthDate: Date; ageInMonths: number; ageType: AgeType } | null {
    if (!birthdateStr) return null;
    const birthDate = new Date(birthdateStr);
    if (Number.isNaN(birthDate.getTime())) return null;

    const today = referenceDate ?? new Date();
    const ageInMonths =
      (today.getFullYear() - birthDate.getFullYear()) * 12 +
      (today.getMonth() - birthDate.getMonth());
    const threshold = Number.isFinite(thresholdMonths as number) ? (thresholdMonths as number) : undefined;
    if (!Number.isFinite(threshold)) {
      throw new Error('Missing age transition months in billing settings');
    }
    const ageType = (ageInMonths < (threshold as number) ? 'BABY' : 'CHILD') as AgeType;
    return { birthDate, ageInMonths, ageType };
  }

  // Calculate age transition info with support for both timing options
  const ageTransitionInfo = useMemo(() => {
    if (!childId || !childData || !shouldUseEnhanced) {
      return null;
    }

    const birthdateStr = childData.birthdate;
    const today = new Date(options?.calculationDate || Date.now());
    const { ageThresholdMonths } = deriveBillingParams(ageTransitionConfig, !!options?.isSubsidised);
    const thresholdMonths = ageThresholdMonths;
    const parsed = parseBirthdateAndAgeType(birthdateStr, today, thresholdMonths);
    if (!parsed) return null;
    const { birthDate, ageType } = parsed;

    // Use the tariff-version config for transition months
    const transitionMonths = thresholdMonths;

    // Calculate transition date based on age threshold
    const transitionDate = new Date(birthDate);
    transitionDate.setMonth(transitionDate.getMonth() + transitionMonths);

    // Determine effective transition date based on timing setting
    let effectiveTransitionDate = transitionDate;
    if (ageTransitionConfig.ageTransitionTiming === 'FOLLOWING_MONTH') {
      // Transition happens on the first day of the following month
      effectiveTransitionDate = new Date(transitionDate.getFullYear(), transitionDate.getMonth() + 1, 1);
    }

    return {
      currentAgeType: ageType,
      transitionDate: effectiveTransitionDate,
      hasTransition: effectiveTransitionDate > today && effectiveTransitionDate < new Date(today.getFullYear(), today.getMonth() + 1, 0)
    };
  }, [childId, childData, shouldUseEnhanced, options?.calculationDate, options?.isSubsidised, ageTransitionConfig]);

  // Extract all unique tariff version IDs to fetch
  const tariffVersionIds = Object.values(normalizedTariffVersions);

  // Extract individual IDs for hook calls (React rules compliance)
  const tariffId1 = tariffVersionIds[0] || '';
  const tariffId2 = tariffVersionIds[1] || '';
  const tariffId3 = tariffVersionIds[2] || '';
  const tariffId4 = tariffVersionIds[3] || '';
  const tariffId5 = tariffVersionIds[4] || '';

  // Call hooks at top level for all tariff versions (React rules compliance)
  // Use version-specific rates to ensure correct data per selected tariff
  const tariffQuery1 = useTariffRates(tariffId1, { enabled: !!tariffId1 });
  const tariffQuery2 = useTariffRates(tariffId2, { enabled: !!tariffId2 });
  const tariffQuery3 = useTariffRates(tariffId3, { enabled: !!tariffId3 });
  const tariffQuery4 = useTariffRates(tariffId4, { enabled: !!tariffId4 });
  const tariffQuery5 = useTariffRates(tariffId5, { enabled: !!tariffId5 });

  // Map tariff data by version ID for lookup (memoized to prevent useEffect re-runs)
  const tariffDataMap = useMemo(() => {
    const map: Record<string, { rates?: unknown[] } | null> = {};
    if (tariffId1) map[tariffId1] = { rates: Array.isArray(tariffQuery1.data) ? (tariffQuery1.data as unknown[]) : [] };
    if (tariffId2) map[tariffId2] = { rates: Array.isArray(tariffQuery2.data) ? (tariffQuery2.data as unknown[]) : [] };
    if (tariffId3) map[tariffId3] = { rates: Array.isArray(tariffQuery3.data) ? (tariffQuery3.data as unknown[]) : [] };
    if (tariffId4) map[tariffId4] = { rates: Array.isArray(tariffQuery4.data) ? (tariffQuery4.data as unknown[]) : [] };
    if (tariffId5) map[tariffId5] = { rates: Array.isArray(tariffQuery5.data) ? (tariffQuery5.data as unknown[]) : [] };
    return map;
  }, [
    tariffId1,
    tariffId2,
    tariffId3,
    tariffId4,
    tariffId5,
    tariffQuery1.data,
    tariffQuery2.data,
    tariffQuery3.data,
    tariffQuery4.data,
    tariffQuery5.data,
  ]);

  // Helper to get the appropriate week factor for a service
  // serviceCode is optional for the same reason as findMatchingRateStrict: an
  // undefined code finds no service and already falls through to 0.
  const getServiceWeekFactor = useCallback((serviceCode: string | undefined, isSubsidised: boolean = false): number => {
    const service = services.find(s => s.code === serviceCode);
    if (!service) {
      // Caller must ensure service exists; fall back to 0 to avoid crashes
      return 0;
    }
    if (service.actual_days === true) {
      return 0; // actual-days calculation
    }
    if (isSubsidised && service.week_factor_subsidised !== undefined) {
      return service.week_factor_subsidised;
    }
    if (service.week_factor !== undefined) {
      return service.week_factor;
    }
    // Default to 0 if factor is missing to avoid throwing during preview
    return 0;
  }, [services]);

  // countWeekdaysInPeriod imported from dateUtils

  // legacy calculation removed

  // legacy normalization removed

  // Helper function to calculate costs for a specific period with proper week factor support
  const calculatePeriodCosts = useCallback((
    startDate: Date,
    endDate: Date,
    ageType: AgeType,
    _reason: string,
    isSubsidised: boolean = false
  ): PeriodCostBreakdown | null => {
    const hasNoServices = !normalizedServicePattern ||
      Object.keys(normalizedServicePattern).length === 0 ||
      !Object.values(normalizedServicePattern).some(groupPattern =>
        Object.values(groupPattern).some(services => services && services.length > 0)
      );
    if (hasNoServices || !tariffDataMap) return null;
    if (!services || services.length === 0) return null;

    const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    const dailyCosts: Record<string, { services: string[]; amount: number; details: Array<{group: string; services: string[]; amount: number}> }> = {};
    let weeklyTotal = 0;
    let monthlyEstimate = 0;

    // dayNameToNumber imported from dateUtils

    // Calculate costs for each group
    Object.entries(normalizedServicePattern).forEach(([groupType, groupPattern]) => {
      if (!groupPattern || Object.keys(groupPattern).length === 0) {
        return;
      }

      const tariffVersionId = normalizedTariffVersions[groupType];
      if (!tariffVersionId) {
        return;
      }

      const tariffDetails = tariffDataMap[tariffVersionId];
      const rawRates = tariffDetails?.rates;
      let strictRates: ReturnType<typeof normalizeAndValidateRates> = [];
      try {
        strictRates = normalizeAndValidateRates(rawRates || [], groupType);
      } catch (_e) {
        // Skip this group if rates payload is invalid or incomplete
        return;
      }
      if (strictRates.length === 0) {
        return;
      }

      Object.entries(groupPattern).forEach(([day, dayServices]) => {
        if (!dayServices || dayServices.length === 0) return;

        const dayOfWeek = dayNameToNumber[day.toLowerCase()];
        if (!dayOfWeek) return;

        let dayTotal = 0;
        const serviceNames: string[] = [];

        dayServices.forEach(service => {
          // Find the matching rate with subsidy awareness
          const rate = findMatchingRateStrict(strictRates, ageType, groupType, service.serviceCode, dayOfWeek, isSubsidised === true, true);

          if (rate && rate.daily_rate) {
            // Verify service exists in organization settings; if missing, skip gracefully
            const serviceDetail = services.find(s => s.code === service.serviceCode);
            if (!serviceDetail) {
              return; // skip unknown service codes
            }
            const weekFactor = getServiceWeekFactor(service.serviceCode, isSubsidised);

            if (serviceDetail?.actual_days === true || weekFactor === 0) {
              // Service uses actual days - count actual occurrences in period
              const actualDays = countWeekdaysInPeriod(dayOfWeek, startDate, endDate);
              const serviceMonthlyAmount = rate.daily_rate * actualDays;
              monthlyEstimate += serviceMonthlyAmount;
            } else {
              // Service uses week factor method
              const serviceMonthlyAmount = Math.round(rate.daily_rate * weekFactor);
              monthlyEstimate += serviceMonthlyAmount;
            }

            dayTotal += rate.daily_rate;
            // Display text only — `undefined` and "" render and join identically.
            const displayName = serviceDetail?.name || service.serviceCode || "";
            serviceNames.push(displayName);
          }
        });

        if (dayTotal > 0) {
          if (!dailyCosts[day]) {
            dailyCosts[day] = {
              services: [],
              amount: 0,
              details: [],
            };
          }
          dailyCosts[day].amount += dayTotal;
          dailyCosts[day].services.push(...serviceNames.map(s => `${groupType}: ${s}`));
          dailyCosts[day].details.push({
            group: groupType,
            services: serviceNames,
            amount: dayTotal,
          });
          weeklyTotal += dayTotal;
        }
      });
    });

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      ageType,
      days,
      daily: dailyCosts,
      weekly: weeklyTotal,
      monthly: monthlyEstimate,
      hasTransition: false,
    };
  }, [normalizedServicePattern, normalizedTariffVersions, tariffDataMap, services, getServiceWeekFactor]);

  // Helper function to apply discounts to amount.
  // NOTE: this is a full-month cost ESTIMATE for the contract wizard, not a
  // partial-month invoice preview. Fixed amounts are subtracted in full here.
  // The backend invoice engine pro-rates custom fixed-amount discounts for
  // partial entry/exit months (see DiscountService.applyCustomDiscounts), so the
  // first/last actual invoice may show a smaller fixed discount than this estimate.
  const applyDiscountsToAmount = useCallback((amount: number, discounts: DiscountsData): number => {
    let discountedAmount = amount;

    // Apply weekday discounts (fixed amounts)
    if (discounts.weekday) {
      Object.values(discounts.weekday).forEach(discount => {
        discountedAmount -= discount;
      });
    }

    // Apply custom discounts
    if (discounts.custom) {
      discounts.custom.forEach(discount => {
        if (discount.type === 'amount') {
          discountedAmount -= discount.value;
        } else if (discount.type === 'percentage') {
          discountedAmount -= (discountedAmount * discount.value) / 100;
        }
      });
    }

    return Math.max(0, discountedAmount); // Ensure non-negative
  }, []);

  // Helper function to calculate group breakdowns (defined once, before usage)
  const calculateGroupBreakdowns = useCallback((periods: PeriodCostBreakdown[]): Record<string, { totalAmount: number; weekly: number; daily: Record<string, { services: string[]; amount: number }>; periods: PeriodCostBreakdown[] }> => {
    const byGroup: Record<string, { totalAmount: number; weekly: number; daily: Record<string, { services: string[]; amount: number }>; periods: PeriodCostBreakdown[] }> = {};
    periods.forEach((p) => {
      // Build per-group daily map using exact per-group amounts from details
      const perGroupDaily: Record<string, Record<string, { services: string[]; amount: number }>> = {};
      Object.entries(p.daily || {}).forEach(([day, info]) => {
        const details = info.details || [];
        if (details.length === 0) return;
        details.forEach((d) => {
          const groupAmount = typeof d.amount === 'number' ? d.amount : 0;
          if (!perGroupDaily[d.group]) perGroupDaily[d.group] = {};
          if (!perGroupDaily[d.group][day]) perGroupDaily[d.group][day] = { services: [], amount: 0 };
          perGroupDaily[d.group][day].amount += groupAmount;
          perGroupDaily[d.group][day].services.push(...d.services);
        });
      });
      Object.entries(perGroupDaily).forEach(([group, daily]) => {
        if (!byGroup[group]) byGroup[group] = { totalAmount: 0, weekly: 0, daily: {}, periods: [] };
        const weekly = Object.values(daily).reduce((s, x) => s + x.amount, 0);
        // Monthly share proportional to weekly share of this period
        const periodWeeklyTotal = Object.values(p.daily || {}).reduce((s, x) => s + x.amount, 0) || 0;
        const share = periodWeeklyTotal > 0 ? weekly / periodWeeklyTotal : 0;
        const monthlyShare = Math.round(p.monthly * share);
        byGroup[group].weekly += weekly;
        byGroup[group].totalAmount += monthlyShare;
        Object.entries(daily).forEach(([day, v]) => {
          if (!byGroup[group].daily[day]) byGroup[group].daily[day] = { services: [], amount: 0 };
          byGroup[group].daily[day].amount += v.amount;
          byGroup[group].daily[day].services.push(...v.services);
        });
        byGroup[group].periods.push({ ...p });
      });
    });
    return byGroup;
  }, []);

  // Enhanced cost calculation function (defined once, before usage)
  const calculateEnhancedCosts = useCallback(() => {
    // Check if service pattern actually has services (nested structure)
    const hasNoServices = !normalizedServicePattern ||
      Object.keys(normalizedServicePattern).length === 0 ||
      !Object.values(normalizedServicePattern).some(groupPattern =>
        Object.values(groupPattern).some(services => services && services.length > 0)
      );

    if (!childId || !childData || hasNoServices || !ageTransitionInfo || !services || services.length === 0) {
      return;
    }

    const newWarnings: string[] = [];
    let totalAmount = 0;

    // Build periods based on view mode
    const periods: PeriodCostBreakdown[] = [];
    const wantsFull = !!options?.contractStartDate && options?.fullContractView;

    if (wantsFull) {
      const contractStart = new Date(options!.contractStartDate!);
      const contractEnd = options?.contractEndDate
        ? new Date(options.contractEndDate)
        : new Date(contractStart.getFullYear(), contractStart.getMonth() + 1, 0);

      // Compute transition date based on child's birthdate, threshold and timing
      const child = childData;
      const birthdateStr = child?.birthdate;
      const birthDate = birthdateStr ? new Date(birthdateStr) : null;
      const { ageThresholdMonths } = deriveBillingParams(ageTransitionConfig, !!options?.isSubsidised);
      const thresholdMonths = ageThresholdMonths;
      let transitionDate = new Date(0);
      if (birthDate && Number.isFinite(birthDate.getTime())) {
        const raw = new Date(birthDate);
        raw.setMonth(raw.getMonth() + thresholdMonths);
        if (ageTransitionConfig.ageTransitionTiming === 'FOLLOWING_MONTH') {
          transitionDate = new Date(raw.getFullYear(), raw.getMonth() + 1, 1);
        } else {
          transitionDate = raw;
        }
      } else {
        transitionDate = ageTransitionInfo.transitionDate; // fallback
      }
      const withinRange = transitionDate > contractStart && transitionDate <= contractEnd;

      const computeSampleMonth = (d: Date) => ({
        ms: new Date(d.getFullYear(), d.getMonth(), 1),
        me: new Date(d.getFullYear(), d.getMonth() + 1, 0),
      });

      if (withinRange) {
        // Period 1: BABY from contract start to day before transition
        const seg1Start = contractStart;
        const seg1End = new Date(transitionDate.getTime() - 24 * 60 * 60 * 1000);
        const { ms: s1ms, me: s1me } = computeSampleMonth(seg1Start);
        const p1 = calculatePeriodCosts(
          s1ms,
          s1me,
          'BABY',
          'Full contract pre-transition',
          options?.isSubsidised ?? false
        );
        if (p1) {
          const days1 = Math.max(0, Math.ceil((seg1End.getTime() - seg1Start.getTime()) / (24 * 60 * 60 * 1000)) + 1);
          periods.push({ ...p1, startDate: seg1Start.toISOString(), endDate: seg1End.toISOString(), days: days1 });
          totalAmount += p1.monthly;
        }

        // Period 2: CHILD from transition to contract end
        const seg2Start = transitionDate;
        const seg2End = contractEnd;
        const { ms: s2ms, me: s2me } = computeSampleMonth(seg2Start);
        const p2 = calculatePeriodCosts(
          s2ms,
          s2me,
          'CHILD',
          'Full contract post-transition',
          options?.isSubsidised ?? false
        );
        if (p2) {
          const days2 = Math.max(0, Math.ceil((seg2End.getTime() - seg2Start.getTime()) / (24 * 60 * 60 * 1000)) + 1);
          periods.push({ ...p2, startDate: seg2Start.toISOString(), endDate: seg2End.toISOString(), days: days2 });
          totalAmount += p2.monthly;
        }
      } else {
        // Single period across full contract
        const ageType: AgeType = contractStart < transitionDate ? 'BABY' : 'CHILD';
        const { ms, me } = computeSampleMonth(contractStart);
        const sp = calculatePeriodCosts(
          ms,
          me,
          ageType,
          'Full contract period',
          options?.isSubsidised ?? false
        );
        if (sp) {
          const days = Math.max(0, Math.ceil((contractEnd.getTime() - contractStart.getTime()) / (24 * 60 * 60 * 1000)) + 1);
          periods.push({ ...sp, startDate: contractStart.toISOString(), endDate: contractEnd.toISOString(), days });
          totalAmount += sp.monthly;
        }
      }
    } else {
      // Single-month preview based on calculationDate
      const today = new Date(options?.calculationDate || Date.now());
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);

      // If requested, lock rates to the age type of the calculation month and avoid splitting
      if (options?.lockAgeToCalculationMonth === true) {
        const singlePeriod = calculatePeriodCosts(
          monthStart,
          monthEnd,
          ageTransitionInfo.currentAgeType,
          'Locked to calculation month',
          options?.isSubsidised ?? false
        );
        if (singlePeriod) {
          periods.push(singlePeriod);
          totalAmount += singlePeriod.monthly;
        }
      } else if (ageTransitionInfo.hasTransition) {
        const transitionDate = ageTransitionInfo.transitionDate;
        const period1 = calculatePeriodCosts(
          monthStart,
          new Date(transitionDate.getTime() - 24 * 60 * 60 * 1000),
          ageTransitionInfo.currentAgeType,
          'Period before age transition',
          options?.isSubsidised ?? false
        );
        if (period1) {
          periods.push(period1);
          totalAmount += period1.monthly;
        }

        const nextAgeType: AgeType = ageTransitionInfo.currentAgeType === 'BABY' ? 'CHILD' : 'BABY';
        const period2 = calculatePeriodCosts(
          transitionDate,
          monthEnd,
          nextAgeType,
          'Period after age transition',
          options?.isSubsidised ?? false
        );
        if (period2) {
          periods.push(period2);
          totalAmount += period2.monthly;
        }
      } else if (!options?.lockAgeToCalculationMonth) {
        const singlePeriod = calculatePeriodCosts(
          monthStart,
          monthEnd,
          ageTransitionInfo.currentAgeType,
          'Single period calculation',
          options?.isSubsidised ?? false
        );
        if (singlePeriod) {
          periods.push(singlePeriod);
          totalAmount += singlePeriod.monthly;
        }
      }
    }

    const serviceTimesByDay: Record<string, Array<{ group: string; service: string; start: string; end: string }>> = {};
    Object.entries(normalizedServicePattern).forEach(([groupType, groupPattern]) => {
      Object.entries(groupPattern || {}).forEach(([day, dayServices]) => {
        if (!serviceTimesByDay[day]) serviceTimesByDay[day] = [];
        dayServices?.forEach((svc) => {
          const def = services.find((s) => s.code === svc.serviceCode);
          if (!def) {
            return; // skip if service definition is missing
          }
          const name = def.name || svc.serviceCode || "";
          serviceTimesByDay[day].push({ group: groupType, service: name, start: def.time_from, end: def.time_to });
        });
      });
    });
    newWarnings.push(...detectOverlapsUtil(serviceTimesByDay, i18n));

    const totalAmountBeforeDiscounts = totalAmount;
    if (discounts) {
      totalAmount = applyDiscountsToAmount(totalAmount, discounts);
    }

    const enhancedResult: EnhancedMultiGroupCostResult = {
      totalCostBreakdown: {
        totalAmount,
        totalAmountBeforeDiscounts,
        periods,
        hasTransition:
          (options?.fullContractView && periods.length > 1) ||
          ageTransitionInfo.hasTransition,
        transitionDate: ageTransitionInfo.transitionDate
          ? ageTransitionInfo.transitionDate.toISOString()
          : undefined,
        transitionReason: ageTransitionInfo.transitionDate ? 'Age threshold reached' : undefined,
      },
      groupCostBreakdowns: calculateGroupBreakdowns(periods),
      warnings: newWarnings,
    };

    const nextHash = JSON.stringify({
      totalCostBreakdown: enhancedResult.totalCostBreakdown,
      groupCostBreakdowns: enhancedResult.groupCostBreakdowns,
      warnings: enhancedResult.warnings,
    });

    if (enhancedResultHashRef.current !== nextHash) {
      enhancedResultHashRef.current = nextHash;
      setEnhancedResult(enhancedResult);
    }
  }, [
    childId,
    childData,
    normalizedServicePattern,
    ageTransitionInfo,
    discounts,
    options,
    services,
    i18n,
    ageTransitionConfig,
    calculateGroupBreakdowns,
    applyDiscountsToAmount,
    calculatePeriodCosts,
  ]);

  

  useEffect(() => {
    const hasNoServices = !normalizedServicePattern ||
      Object.keys(normalizedServicePattern).length === 0 ||
      !Object.values(normalizedServicePattern).some(groupPattern =>
        Object.values(groupPattern).some(services => services && services.length > 0)
      );
    if (!childId || !childData || hasNoServices || !services || services.length === 0) {
      enhancedResultHashRef.current = null;
      setEnhancedResult(null);
      return;
    }

    if (ageTransitionInfo) {
      calculateEnhancedCosts();
    }
  }, [normalizedServicePattern, childId, childData, services, i18n, ageTransitionInfo, discounts, options?.calculationDate, options?.isSubsidised, options?.contractStartDate, options?.contractEndDate, options?.fullContractView, options?.lockAgeToCalculationMonth, calculateEnhancedCosts]);

  return enhancedResult ?? {
    totalCostBreakdown: { totalAmount: 0, periods: [], hasTransition: false },
    groupCostBreakdowns: {},
    warnings: [],
  };
}
